import { message } from '@client/components/_ui/feedback/message';
import { notification } from '@client/components/_ui/feedback/notification';
import { debounce } from '@client/utils/common/general';
import { textToHtml } from '@client/utils/common/text-to-html';
import type React from 'react';

/**
 * Converts camelCase to Title Case With Spaces
 */
function humanizeFieldName(fieldName: string): string {
    const withSpaces = fieldName.replace(/([A-Z])/g, ' $1').trim();
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
}

/**
 * Creates a human-readable field name from a Zod path array
 */
function createFieldNameFromPath(path: (string | number)[]): string {
    if (path.length === 0) return 'Unknown Field';

    // Filter out UUIDs, array indices, and "set", then humanize the remaining parts
    const meaningfulParts = path.filter((part) => {
        if (typeof part === 'number') return false;
        if (typeof part === 'string') {
            // Check if it's a UUID pattern
            const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
            if (uuidPattern.test(part)) return false;

            // Filter out "set" (case-insensitive)
            if (part.toLowerCase() === 'set') return false;
        }
        return true;
    });

    if (meaningfulParts.length === 0) return 'Field';

    // Humanize and join the parts
    return meaningfulParts.map((part) => humanizeFieldName(String(part))).join(' - ');
}

function formatErrorMessage(error: Error | string | unknown): React.ReactNode {
    // Debug logging to see the actual error structure
    console.log('Error object:', error);

    // Handle TRPCClientError with JSON message containing Zod issues
    if (error instanceof Error) {
        console.log('Error message:', error.message);

        // Try to parse the error message as JSON (for TRPCClientError)
        try {
            const parsedMessage = JSON.parse(error.message);
            if (Array.isArray(parsedMessage)) {
                console.log('Found JSON array in error message:', parsedMessage);

                // Check if it looks like Zod issues
                const isZodIssues = parsedMessage.every(
                    (item) =>
                        item &&
                        typeof item === 'object' &&
                        'path' in item &&
                        'message' in item &&
                        Array.isArray(item.path),
                );

                if (isZodIssues) {
                    console.log('Parsed Zod issues from error message');
                    const issues = parsedMessage as Array<{
                        path: (string | number)[];
                        message: string;
                        code?: string;
                    }>;

                    const errorItems = issues.map((issue, index) => {
                        const fieldName = createFieldNameFromPath(issue.path);
                        console.log('Issue path:', issue.path, 'Field name:', fieldName);
                        return (
                            <li key={index} className="mb-1.5 leading-tight">
                                <strong className="text-red-600">{fieldName}:</strong> {issue.message}
                            </li>
                        );
                    });

                    return (
                        <div>
                            <p>Please correct the following issues:</p>
                            <ul className="m-0 list-disc pl-5">{errorItems}</ul>
                        </div>
                    );
                }
            }
        } catch {
            console.log('Error message is not valid JSON, continuing with other checks');
        }
    }

    if (error && typeof error === 'object' && 'data' in error && error.data && typeof error.data === 'object') {
        console.log('Error data:', error.data);

        // Check for zodError
        if ('zodError' in error.data && error.data.zodError && typeof error.data.zodError === 'object') {
            const zodError = error.data.zodError;
            console.log('Zod error:', zodError);

            // Handle the issues format (newer Zod format)
            if ('issues' in zodError && Array.isArray(zodError.issues)) {
                console.log('Found issues format');
                const issues = zodError.issues as Array<{
                    path: (string | number)[];
                    message: string;
                    code?: string;
                }>;

                if (issues.length === 0) {
                    return 'Validation error occurred. Please check your input.';
                }

                const errorItems = issues.map((issue, index) => {
                    const fieldName = createFieldNameFromPath(issue.path);
                    console.log('Issue path:', issue.path, 'Field name:', fieldName);
                    return (
                        <li key={index} className="mb-1.5 leading-tight">
                            <strong className="text-red-600">{fieldName}:</strong> {issue.message}
                        </li>
                    );
                });

                return (
                    <div>
                        <p>Please correct the following issues:</p>
                        <ul className="m-0 list-disc pl-5">{errorItems}</ul>
                    </div>
                );
            }

            // Handle the fieldErrors format (older format)
            if ('fieldErrors' in zodError && zodError.fieldErrors && typeof zodError.fieldErrors === 'object') {
                console.log('Found fieldErrors format');
                const fieldErrors = zodError.fieldErrors as Record<string, string[]>;
                console.log('Field errors:', fieldErrors);

                if (Object.keys(fieldErrors).length === 0) {
                    return 'Validation error occurred. Please check your input.';
                }

                const errorItems = Object.entries(fieldErrors).map(([field, messages]) => {
                    // Check if the field might be a path string that needs parsing
                    let fieldName = field;
                    if (field.includes('.') || field.includes('[')) {
                        // Try to parse path-like field names
                        const pathParts = field.split(/[.[\]]+/).filter(Boolean);
                        fieldName = createFieldNameFromPath(pathParts);
                    } else {
                        fieldName = humanizeFieldName(field);
                    }

                    console.log('Field:', field, 'Field name:', fieldName, 'Messages:', messages);

                    return (
                        <li key={field} className="mb-1.5 leading-tight">
                            <strong className="text-red-600">{fieldName}:</strong> {messages.join(', ')}
                        </li>
                    );
                });

                return (
                    <div>
                        <p>Please correct the following issues:</p>
                        <ul className="m-0 list-disc pl-5">{errorItems}</ul>
                    </div>
                );
            }
        }

        // Check if the error itself contains issues directly
        if ('issues' in error.data && Array.isArray(error.data.issues)) {
            console.log('Found issues directly in error.data');
            const issues = error.data.issues as Array<{
                path: (string | number)[];
                message: string;
                code?: string;
            }>;

            if (issues.length === 0) {
                return 'Validation error occurred. Please check your input.';
            }

            const errorItems = issues.map((issue, index) => {
                const fieldName = createFieldNameFromPath(issue.path);
                console.log('Direct issue path:', issue.path, 'Field name:', fieldName);
                return (
                    <li key={index} className="mb-1.5 leading-tight">
                        <strong className="text-red-600">{fieldName}:</strong> {issue.message}
                    </li>
                );
            });

            return (
                <div>
                    <p>Please correct the following issues:</p>
                    <ul className="m-0 list-disc pl-5">{errorItems}</ul>
                </div>
            );
        }
    }

    // Fallback for other error types (returns string, will be handled by dangerouslySetInnerHTML)
    const errorHtmlString =
        error instanceof Error
            ? textToHtml(error.message)
            : typeof error === 'string'
              ? textToHtml(error)
              : error && typeof error === 'object' && 'toString' in error
                ? textToHtml(error.toString())
                : 'An unknown error occurred.';

    return <div dangerouslySetInnerHTML={{ __html: errorHtmlString }} />;
}

export function isCancelledError(error: Error | string | unknown): boolean {
    if (error instanceof Error) {
        return (
            error.name === 'CancelledError' ||
            error.name === 'CanceledError' ||
            error.name === 'AbortError' ||
            error.message === 'CancelledError' ||
            error.message === 'CanceledError' ||
            error.message === 'Aborted' ||
            error.message === 'AbortError'
        );
    }

    if (typeof error === 'string') {
        return error === 'CancelledError' || error === 'CanceledError' || error === 'AbortError' || error === 'Aborted';
    }

    return false;
}

export const errorNotification = debounce((error: Error | string | unknown) => {
    if (isCancelledError(error)) {
        return;
    }

    message.destroy();

    const descriptionContent = formatErrorMessage(error);

    notification.error({
        title: 'Error',
        description: descriptionContent,
        showProgress: true,
        pauseOnHover: true,
        duration: 7,
    });
}, 300);
